import React, { useState } from 'react';
import { Bell, Shield, CreditCard, HelpCircle, LogOut } from 'lucide-react';

export const SettingsPage = ({ onLogout }) => {
  const [notifications, setNotifications] = useState({
    orderUpdates: true,
    promotions: true,
    rewards: true,
    newsletter: false
  });

  const [privacy, setPrivacy] = useState({
    shareData: false,
    analytics: true,
    marketing: false
  });

  const toggleNotification = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePrivacy = (key) => {
    setPrivacy(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const ToggleSwitch = ({ enabled, onChange }) => (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-green-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your account preferences and settings</p>
      </div>

      <div className="space-y-8">
        {/* Notifications */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Bell size={24} className="text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">Notifications</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Order Updates</h3>
                <p className="text-sm text-gray-600">Get notified about your order status</p>
              </div>
              <ToggleSwitch 
                enabled={notifications.orderUpdates} 
                onChange={() => toggleNotification('orderUpdates')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Promotions & Offers</h3>
                <p className="text-sm text-gray-600">Receive special deals and discounts</p>
              </div>
              <ToggleSwitch 
                enabled={notifications.promotions} 
                onChange={() => toggleNotification('promotions')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Reward Updates</h3>
                <p className="text-sm text-gray-600">Get notified about coin earnings and rewards</p>
              </div>
              <ToggleSwitch 
                enabled={notifications.rewards} 
                onChange={() => toggleNotification('rewards')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Newsletter</h3>
                <p className="text-sm text-gray-600">Monthly updates and restaurant news</p>
              </div>
              <ToggleSwitch 
                enabled={notifications.newsletter} 
                onChange={() => toggleNotification('newsletter')} 
              />
            </div>
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={24} className="text-green-600" />
            <h2 className="text-2xl font-bold text-gray-800">Privacy & Data</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Share Usage Data</h3>
                <p className="text-sm text-gray-600">Help improve our service with anonymous usage data</p>
              </div>
              <ToggleSwitch 
                enabled={privacy.shareData} 
                onChange={() => togglePrivacy('shareData')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Analytics</h3>
                <p className="text-sm text-gray-600">Allow analytics to personalize your experience</p>
              </div>
              <ToggleSwitch 
                enabled={privacy.analytics} 
                onChange={() => togglePrivacy('analytics')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div>
                <h3 className="font-semibold text-gray-800">Marketing Communications</h3>
                <p className="text-sm text-gray-600">Receive targeted marketing based on your preferences</p>
              </div>
              <ToggleSwitch 
                enabled={privacy.marketing} 
                onChange={() => togglePrivacy('marketing')} 
              />
            </div>
          </div>
        </div>

        {/* Account Actions */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Account</h2>
          
          <div className="space-y-4">
            <button className="flex items-center gap-4 w-full p-4 text-left hover:bg-gray-50 rounded-lg transition-colors">
              <CreditCard size={20} className="text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-800">Payment Methods</h3>
                <p className="text-sm text-gray-600">Manage your saved payment methods</p>
              </div>
            </button>
            
            <button className="flex items-center gap-4 w-full p-4 text-left hover:bg-gray-50 rounded-lg transition-colors">
              <HelpCircle size={20} className="text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-800">Help & Support</h3>
                <p className="text-sm text-gray-600">Get help or contact customer support</p>
              </div>
            </button>
            
            <button 
            
              className="flex items-center gap-4 w-full p-4 text-left hover:bg-red-50 rounded-lg transition-colors text-red-600"
            >
              <LogOut size={20} />
              <div>
                <h3 className="font-semibold" onClick={onLogout}>Sign Out</h3>
                <p className="text-sm text-red-500">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};